import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import chest from '~/assets/icons/chest.svg';
import promocode from '~/assets/icons/promocode.svg';
import wallet from '~/assets/icons/wallet.svg';
import { CurrencyIcon } from '~/components/collections/CurrencyIcon';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { Currency, getRandomInt, TechTuningCaseType } from '~/shared';
import {
	setCaseResultItem,
	setCaseResultVisible,
	useLayoutDispatch,
} from '../../redux';
import { CountPicker } from '../CountPicker';
import {
	caseAppearDelay,
	caseColors,
	caseDecDotsImages,
	caseImages,
} from './CaseItem.model';
import {
	StyledBg,
	StyledBottomContainer,
	StyledButtonContent,
	StyledButtonsGrid,
	StyledCaseDecSvg,
	StyledContainer,
	StyledCountContainer,
	StyledCountInputContainer,
	StyledDiscountContainer,
	StyledHead,
	StyledHeadRight,
	StyledImage,
	StyledName,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledTitle,
} from './CaseItem.styled';

const CaseItem = ({
	type,
	selected = false,
	hidden = false,
	handleSelect,
	handleBack,
}: {
	type: TechTuningCaseType;
	selected?: boolean;
	hidden?: boolean;
	handleSelect: () => void;
	handleBack: () => void;
}) => {
	const dispatch = useLayoutDispatch();
	const cases = useObservable(rage.techTuning.getCases());
	const caseConfig = cases?.[type] ?? null;
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const [count, setCount] = useState(1);
	const [discountCheckTick, setDiscountCheckTick] = useState(0);
	const inStock = useMemo(() => caseConfig?.inStock ?? 0, [caseConfig]);
	const owned = useMemo(() => inStock > 0, [inStock]);
	const [dur1, dur2] = useMemo(
		() => [getRandomInt(20, 40), getRandomInt(15, 30)],
		[],
	);

	useEffect(() => {
		if (!caseConfig?.discount) {
			return;
		}

		setDiscountCheckTick((tick) => tick + 1);

		const interval = setInterval(() => {
			setDiscountCheckTick((tick) => tick + 1);
		}, 60_000);

		return () => clearInterval(interval);
	}, [
		caseConfig?.discount,
		caseConfig?.discountTimestampStart,
		caseConfig?.discountTimestampEnd,
	]);

	const discountActive = useMemo(() => {
		if (!caseConfig?.discount) {
			return false;
		}

		const now = Date.now();
		const { discountTimestampStart, discountTimestampEnd } = caseConfig;

		if (discountTimestampStart && now < discountTimestampStart) {
			return false;
		}

		if (discountTimestampEnd && now > discountTimestampEnd) {
			return false;
		}

		return true;
	}, [caseConfig, discountCheckTick]);

	const handleOpen = useCallback(async () => {
		const { itemId } = await rage.techTuning.openCase(type);
		dispatch(setCaseResultVisible(true));
		dispatch(setCaseResultItem(itemId));
	}, [type]);

	const handleBuy = useCallback(
		async (caseType: TechTuningCaseType, count: number) => {
			await rage.techTuning.buyCase(caseType, count);
		},
		[],
	);

	const displayPrice = useMemo(() => {
		if (!caseConfig) {
			return 0;
		}

		const discount = discountActive ? caseConfig.discount ?? 0 : 0;
		const priceWithDiscount = Math.floor(caseConfig.price * (1 - discount));

		if (owned) {
			return priceWithDiscount;
		}

		return priceWithDiscount * count;
	}, [owned, caseConfig, count, discountActive]);

	const displayOldPrice = useMemo(() => {
		if (!discountActive || !caseConfig) {
			return null;
		}

		const casePrice = caseConfig.price;
		return owned ? casePrice : casePrice * count;
	}, [owned, caseConfig, count, discountActive]);

	useEffect(() => {
		if (owned) {
			setCount(inStock);
		}
	}, [owned, inStock]);

	return (
		<StyledContainer
			className="caseItem"
			$delay={caseAppearDelay[type]}
			$hidden={hidden}
		>
			<StyledBg style={{ backgroundColor: caseColors[type] }} />
			<StyledCaseDecSvg
				width="400"
				height="400"
				viewBox="0 0 400 400"
				style={{ opacity: selected ? 0 : 1 }}
			>
				<circle
					opacity="0.2"
					cx="200"
					cy="200"
					r="129.5"
					fill={'none'}
					stroke={caseColors[type]}
				/>
				<circle
					opacity="0.1"
					cx="200"
					cy="200"
					r="169.5"
					fill={'none'}
					stroke={caseColors[type]}
				/>
				<g>
					<image
						href={caseDecDotsImages[type]}
						x="161.5"
						y="55.5"
						width="34"
						height="34"
					/>
					<animateTransform
						attributeName="transform"
						attributeType="XML"
						type="rotate"
						from={`0 200 200`}
						to={`360 200 200`}
						dur={`${dur1}s`}
						begin={`${0}s`}
						repeatCount="indefinite"
					/>
				</g>
				<g>
					<image
						href={caseDecDotsImages[type]}
						x="161.5"
						y="15"
						width="34"
						height="34"
					/>
					<animateTransform
						attributeName="transform"
						attributeType="XML"
						type="rotate"
						from={`0 200 200`}
						to={`-360 200 200`}
						dur={`${dur2}s`}
						begin={`${0}s`}
						repeatCount="indefinite"
					/>
				</g>
			</StyledCaseDecSvg>
			<StyledHead>
				<StyledTitle>
					<Text>Тюнинг кейс</Text>
				</StyledTitle>
				<StyledHeadRight>
					{owned ? (
						<StyledCountContainer>
							<img src={chest} />
							<Text>{inStock}</Text>
						</StyledCountContainer>
					) : null}
					{discountActive && (caseConfig?.discount ?? 0) > 0 ? (
						<StyledDiscountContainer>
							<img src={promocode} />
							<div className="divider">
								<Divider $direction="V" />
								<Divider $direction="V" $color="#000000" />
							</div>
							<Text>{Math.round((caseConfig?.discount ?? 0) * 100)}%</Text>
						</StyledDiscountContainer>
					) : null}
				</StyledHeadRight>
			</StyledHead>
			<StyledName>Тюнинг кейс</StyledName>
			<StyledImage
				src={caseImages[type]}
				style={{ height: selected ? 190 : 254 }}
			/>
			<SwitchTransition>
				<CSSTransition
					timeout={300}
					classNames={'bottom'}
					key={selected ? 'selected' : 'notSelected'}
					addEndListener={(node, done) =>
						node.addEventListener('transitionend', done, false)
					}
				>
					<StyledBottomContainer style={{ marginTop: selected ? 27 : 60 }}>
						{selected && (
							<StyledCountInputContainer>
								<Text>{owned ? 'В наличии' : 'Выберите кол-во'}</Text>
								<CountPicker
									value={count}
									onChange={setCount}
									disabled={owned}
									min={1}
									max={99}
									placeholder="шт."
								/>
							</StyledCountInputContainer>
						)}
						<Text
							style={{
								fontSize: 16,
								color: '#FFFFFF',
								opacity: owned && selected ? 0 : 0.25,
								transition: 'opacity 0.3s ease',
							}}
						>
							Стоимость товара
						</Text>
						<StyledPriceContainer $hidden={owned && selected}>
							<StyledPrice>
								<CurrencyIcon icon={rubleIcon} size={'lg'} />
								<Text>{displayPrice.toLocaleString('ru-RU')}</Text>
							</StyledPrice>
							{displayOldPrice ? (
								<StyledOldPrice>
									<CurrencyIcon icon={rubleIcon} size={'sm'} />
									<Text>{displayOldPrice.toLocaleString('ru-RU')}</Text>
									<div className="line" />
								</StyledOldPrice>
							) : null}
						</StyledPriceContainer>
						{selected ? (
							<StyledButtonsGrid>
								<Button
									color={ButtonColors.Default}
									height={ButtonHeight.Height50}
									onClick={handleBack}
								>
									<StyledButtonContent>
										<img width={14} height={14} src={arrowLeft} />
										<Text>Вернуться</Text>
									</StyledButtonContent>
								</Button>
								{owned ? (
									<Button
										color={ButtonColors.Select}
										height={ButtonHeight.Height50}
										onClick={(e) => {
											e.currentTarget.blur();
											handleOpen().catch((err) =>
												rage.notifications.showException(err),
											);
										}}
									>
										<StyledButtonContent>
											<img width={16} height={16} src={chest} />
											<Text>Открыть</Text>
										</StyledButtonContent>
									</Button>
								) : (
									<Button
										color={ButtonColors.Buy}
										height={ButtonHeight.Height50}
										onClick={(e) => {
											e.currentTarget.blur();
											handleBuy(type, count).catch(
												rage.notifications.showException,
											);
										}}
									>
										<StyledButtonContent>
											<img width={16} height={16} src={wallet} />
											<Text>Купить</Text>
										</StyledButtonContent>
									</Button>
								)}
							</StyledButtonsGrid>
						) : (
							<Button
								color={ButtonColors.Select}
								height={ButtonHeight.Height50}
								style={{ marginTop: 30 }}
								onClick={handleSelect}
							>
								<StyledButtonContent>
									<img width={16} height={16} src={chest} />
									<Text>Посмотреть содержимое</Text>
								</StyledButtonContent>
							</Button>
						)}
					</StyledBottomContainer>
				</CSSTransition>
			</SwitchTransition>
		</StyledContainer>
	);
};

export { CaseItem };
